import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, User, Lock } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "@/context/ShopContext";

export default function Connexion() {
  const { user, login } = useShop();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/catalogue";
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate(next, { replace: true }); }, [user, next, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email.trim(), form.password);
      toast.success("Connecté !");
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(err.message || "Échec de la connexion.");
    } finally { setBusy(false); }
  };

  return (
    <section className="mx-auto max-w-md px-5 py-12 md:py-20">
      <Helmet>
        <title>Se connecter — Zafara Bénin</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <div className="text-center">
        <User className="h-10 w-10 mx-auto text-[#0E7A3B]" />
        <h1 className="mt-6 font-display text-4xl text-[#16241B] tracking-tighter">Se connecter</h1>
        <p className="mt-3 text-[#16241B]/70">Accédez à votre compte Zafara (partagé avec l'app mobile).</p>
      </div>

      <form onSubmit={submit} className="mt-10 space-y-4 rounded-3xl border border-[#EDE7D9] bg-white p-6 md:p-8">
        <label className="block">
          <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Email</span>
          <input
            data-testid="login-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B]"
          />
        </label>
        <label className="block">
          <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Mot de passe</span>
          <input
            data-testid="login-password"
            type="password"
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B]"
          />
        </label>
        <button
          data-testid="login-submit"
          type="submit"
          disabled={busy}
          className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#0E7A3B] text-white px-6 py-3.5 font-semibold hover:bg-[#0d6a34] transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          {busy ? "Connexion…" : "Se connecter"}
        </button>
        <p className="text-center text-sm text-[#16241B]/70 pt-2">
          Pas de compte ?{" "}
          <Link to={`/compte/inscription${next !== "/catalogue" ? `?next=${next}` : ""}`} className="text-[#0E7A3B] font-semibold hover:underline">
            Créer un compte
          </Link>
        </p>
      </form>
    </section>
  );
}
